import { getCoreRowModel, getExpandedRowModel } from "@tanstack/react-table"
import type { ExpandedState } from "@tanstack/react-table"
import type { FC } from "react"
import { useState } from "react"

import { type PayFileInvoiceLineFields, usePayFileInvoiceColumns } from "../hooks/usePayFileInvoiceColumns"

import { AerosBaseTable } from "@/components/Tables/Aeros/components/AerosBaseTable"
import { expandedCellStyles } from "@/components/Tables/Aeros/shared"
import type { Invoice } from "@/graphql/codegen/graphql"
import { InvoiceRowDetails } from "@/screens/Accounting/subfeatures/Invoices/components/InvoiceRowDetails"

interface PayFileInvoicesSubTableProps {
  data: PayFileInvoiceLineFields[]
  payFileId: string
  canRemove: boolean
  onSuccess: () => void
}

export const PayFileInvoicesSubTable: FC<PayFileInvoicesSubTableProps> = ({
  data,
  payFileId,
  canRemove,
  onSuccess,
}) => {
  const columns = usePayFileInvoiceColumns({ payFileId, canRemove, onSuccess })
  const [expanded, setExpanded] = useState<ExpandedState>({})

  return (
    <AerosBaseTable<PayFileInvoiceLineFields>
      slotProps={{
        headerCell: { className: "bg-primary-100" },
        container: { ...({ "data-testid": "pay-file.invoices-sub-table.table" } as Record<string, string>) },
        bodyCellExpanded: { className: "bg-primary-100" },
        expandedCell: expandedCellStyles,
      }}
      features={{
        tanstackOptions: {
          data,
          columns,
          state: {
            expanded,
          },
          enableSorting: false,
          getCoreRowModel: getCoreRowModel(),
          getExpandedRowModel: getExpandedRowModel(),
          getRowId: (row) => row.invoice.invoiceId,
          getRowCanExpand: () => true,
          onExpandedChange: setExpanded,
          // PayFileInvoiceLinesDataLoaderService only joins invoice + vendorCompany, so this invoice is
          // narrower than the full generated Invoice type InvoiceRowDetails expects — safe because
          // InvoiceRowDetails (and the sub-components it renders) only read the fields fetched above.
          renderExpandedContent: (row) => <InvoiceRowDetails invoice={row.original.invoice as Invoice} />,
        },
      }}
      noDataMessage='No invoices found'
    />
  )
}
